import { useEffect, useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight, X, Maximize2 } from 'lucide-react';
import './SchoolCurriculumSection.css';

export default function SchoolCurriculumSection({
	title = 'Deux parcours',
	description = 'Des programmes intensifs conçus pour vous permettre d’acquérir des compétences concrètes et accéder rapidement aux métiers les plus demandés dans le secteur IT. ',
	images = [],
	sectionClassName = '',
}) {
	const safeImages = useMemo(() => images.filter(Boolean), [images]);

	const [activeIndex, setActiveIndex] = useState(0);
	const [isLightboxOpen, setIsLightboxOpen] = useState(false);

	useEffect(() => {
		if (activeIndex >= safeImages.length) {
			setActiveIndex(0);
		}
	}, [activeIndex, safeImages.length]);

	const openLightbox = (index) => {
		if (!safeImages.length) return;
		setActiveIndex(index);
		setIsLightboxOpen(true);
	};

	const closeLightbox = () => {
		setIsLightboxOpen(false);
	};

	const goToNext = () => {
		if (!safeImages.length) return;
		setActiveIndex((prev) => (prev + 1) % safeImages.length);
	};

	const goToPrev = () => {
		if (!safeImages.length) return;
		setActiveIndex(
			(prev) => (prev - 1 + safeImages.length) % safeImages.length,
		);
	};

	useEffect(() => {
		if (!isLightboxOpen) return;

		const handleKeyDown = (event) => {
			if (event.key === 'Escape') closeLightbox();
			if (event.key === 'ArrowRight' || event.key === 'ArrowDown') goToNext();
			if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') goToPrev();
		};

		const originalOverflow = document.body.style.overflow;
		document.body.style.overflow = 'hidden';
		window.addEventListener('keydown', handleKeyDown);

		return () => {
			document.body.style.overflow = originalOverflow;
			window.removeEventListener('keydown', handleKeyDown);
		};
	}, [isLightboxOpen, safeImages.length]);

	const handleWheel = (event) => {
		if (!isLightboxOpen) return;

		if (event.deltaY > 0) {
			goToNext();
		} else {
			goToPrev();
		}
	};

	return (
		<>
			<section className={`school-curriculum ${sectionClassName}`.trim()}>
				<div className='school-curriculum__container'>
					<header className='school-curriculum__header'>
						<h2 className='school-curriculum__title'>{title}</h2>
						<span className='school-curriculum__divider' />
						<p className='school-curriculum__text'>{description}</p>
					</header>

					<div className='school-curriculum__grid'>
						{safeImages.map((image, index) => (
							<button
								type='button'
								className='school-curriculum__item'
								key={`${image}-${index}`}
								onClick={() => openLightbox(index)}
								aria-label={`Open curriculum image ${index + 1}`}>
								<img
									className='school-curriculum__image'
									src={image}
									alt={`${title} ${index + 1}`}
								/>

								<span className='school-curriculum__zoom'>
									<Maximize2 size={18} />
								</span>
							</button>
						))}
					</div>
				</div>
			</section>

			{isLightboxOpen && safeImages.length > 0 && (
				<div className='school-lightbox school-lightbox--open'>
					<div className='school-lightbox__backdrop' onClick={closeLightbox} />

					<div className='school-lightbox__shell'>
						<div className='school-lightbox__topbar'>
							<button
								type='button'
								className='school-lightbox__icon-btn'
								onClick={closeLightbox}
								aria-label='Close gallery'>
								<X size={20} />
							</button>
						</div>

						<div className='school-lightbox__content' onWheel={handleWheel}>
							<div className='school-lightbox__stage'>
								<button
									type='button'
									className='school-lightbox__nav school-lightbox__nav--prev'
									onClick={goToPrev}
									aria-label='Previous image'>
									<ChevronLeft size={24} />
								</button>

								<div className='school-lightbox__image-wrap'>
									<img
										key={safeImages[activeIndex]}
										className='school-lightbox__image'
										src={safeImages[activeIndex]}
										alt={`${title} enlarged ${activeIndex + 1}`}
									/>
								</div>

								<button
									type='button'
									className='school-lightbox__nav school-lightbox__nav--next'
									onClick={goToNext}
									aria-label='Next image'>
									<ChevronRight size={24} />
								</button>
							</div>

							<div className='school-lightbox__sidebar'>
								<div className='school-lightbox__thumbs'>
									{safeImages.map((image, index) => (
										<button
											type='button'
											key={`${image}-thumb-${index}`}
											className={`school-lightbox__thumb ${
												index === activeIndex
													? 'school-lightbox__thumb--active'
													: ''
											}`}
											onClick={() => setActiveIndex(index)}
											aria-label={`Go to image ${index + 1}`}>
											<img
												className='school-lightbox__thumb-image'
												src={image}
												alt={`Thumbnail ${index + 1}`}
											/>
										</button>
									))}
								</div>
							</div>
						</div>

						<div className='school-lightbox__counter'>
							{activeIndex + 1} / {safeImages.length}
						</div>
					</div>
				</div>
			)}
		</>
	);
}
